// On-demand user resolution — replaces full-array scans for portal surfaces
// that no longer have the whole customer base pre-loaded (call-center roles).
// Every lookup is a single INDEXED query, so each is tiny and cheap:
//   • /users is indexed on `mobile` and `firstName` (covers ALL usertypes —
//     a phone or first-name lookup resolves drivers, staff, AND customers).
//   • /customer_index is indexed on `searchName` (lowercased "first last") and
//     `mobile`, so customer search supports first-only, first+last, or phone.
//
// Resolved /users records are hydrated into Redux + the IDB cache via
// patchUsersCacheUpsert so a repeat lookup / subsequent .find is free.
import { firebase } from '../config/configureFirebase';
import { get, query, orderByChild, equalTo, startAt, endAt, limitToFirst } from "firebase/database";
import { patchUsersCacheUpsert } from './usersactions';

// Normalize an Ethiopian phone input to the stored +251 E.164 form so the
// indexed `mobile` equalTo lookup matches.
export const normalizeEthiopiaMobile = (s) => {
  let d = (s || '').replace(/[^0-9]/g, '');
  if (d.indexOf('251') === 0) d = d.slice(3);
  if (d.indexOf('0') === 0) d = d.slice(1);
  return '+251' + d;
};

// Resolve a single user (any usertype) by phone via the /users `mobile` index.
// Returns { id, uid, ...record } or null, and hydrates it into Redux + cache.
export const lookupUserByPhone = (rawPhone) => async () => {
  if (!rawPhone) return null;
  const { usersRef } = firebase;
  const formatted = normalizeEthiopiaMobile(rawPhone);
  const snap = await get(query(usersRef, orderByChild('mobile'), equalTo(formatted)));
  const val = snap.val();
  if (!val) return null;
  const id = Object.keys(val)[0];
  const rec = { ...val[id], id, uid: id };
  try { await patchUsersCacheUpsert(id, rec); } catch (_) { /* hydrate best-effort */ }
  return rec;
};

// Resolve users (any usertype) by FIRST-NAME prefix via the /users `firstName`
// index. Returns an array [{ id, uid, ...record }] (full records, hydrated).
export const lookupUsersByNamePrefix = (prefix, limit = 10) => async () => {
  const p = (prefix || '').trim();
  if (!p) return [];
  const { usersRef } = firebase;
  const snap = await get(query(usersRef, orderByChild('firstName'), startAt(p), endAt(p + ''), limitToFirst(limit)));
  const val = snap.val() || {};
  return Object.keys(val).map(id => ({ ...val[id], id, uid: id }));
};

// Customer-scoped name search against the slim /customer_index `searchName`
// (lowercased "first last") — supports first-only AND first+last prefixes.
// Returns slim records stamped usertype:'customer' for navigation.
export const lookupCustomersByName = (prefix, limit = 5) => async () => {
  const p = (prefix || '').trim().toLowerCase();
  if (!p) return [];
  const { customersByNameRef } = firebase;
  const snap = await get(customersByNameRef(p, limit));
  const val = snap.val() || {};
  return Object.keys(val)
    .filter(k => k.indexOf('_') !== 0)
    .map(id => ({ ...val[id], id, uid: id, usertype: 'customer' }));
};

// Customer-scoped phone lookup against the slim /customer_index `mobile` index.
export const lookupCustomerByPhone = (rawPhone) => async () => {
  if (!rawPhone) return null;
  const { customersByMobileRef } = firebase;
  const snap = await get(customersByMobileRef(normalizeEthiopiaMobile(rawPhone)));
  const val = snap.val();
  if (!val) return null;
  const id = Object.keys(val).filter(k => k.indexOf('_') !== 0)[0];
  if (!id) return null;
  return { ...val[id], id, uid: id, usertype: 'customer' };
};

// Convenience single-user resolver. Prefers uid (single ref), then phone, then
// first name. Returns { id, uid, ...record } or null.
export const resolveSingleUser = ({ uid, phone, name } = {}) => async (dispatch) => {
  if (uid) {
    const { singleUserRef } = firebase;
    const snap = await get(singleUserRef(uid));
    const v = snap.val();
    if (!v) return null;
    const rec = { ...v, id: uid, uid };
    try { await patchUsersCacheUpsert(uid, rec); } catch (_) { /* hydrate best-effort */ }
    return rec;
  }
  if (phone) return dispatch(lookupUserByPhone(phone));
  if (name) { const list = await dispatch(lookupUsersByNamePrefix(name, 1)); return (list && list[0]) || null; }
  return null;
};
